import React, { type ReactNode } from 'react';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock3,
  Inbox,
  LoaderCircle,
  RefreshCw,
  ShieldX,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  EXPERIENCE_MOTION_CLASS,
  type ExperienceStatusTone,
} from '../../lib/experience-semantic-styles';

export const EXPERIENCE_STATE_NAMES = [
  'loading',
  'ready',
  'empty',
  'denied',
  'unavailable',
  'stale',
  'error',
  'action_pending',
  'action_committed',
  'action_failed',
  'superseded',
] as const;

export type ExperienceStateName = (typeof EXPERIENCE_STATE_NAMES)[number];

const presentation = {
  loading: {
    Icon: LoaderCircle,
    tone: 'information',
    legacyTone:
      'text-status-information-fg bg-status-information-bg border-status-information-border',
    live: 'polite',
  },
  ready: {
    Icon: CheckCircle2,
    tone: 'success',
    legacyTone: 'text-status-success-fg bg-status-success-bg border-status-success-border',
    live: 'polite',
  },
  empty: {
    Icon: Inbox,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
  denied: {
    Icon: ShieldX,
    tone: 'danger',
    legacyTone: 'text-status-danger-fg bg-status-danger-bg border-status-danger-border',
    live: 'assertive',
  },
  unavailable: {
    Icon: Ban,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
  stale: {
    Icon: RefreshCw,
    tone: 'warning',
    legacyTone: 'text-status-warning-fg bg-status-warning-bg border-status-warning-border',
    live: 'polite',
  },
  error: {
    Icon: AlertCircle,
    tone: 'danger',
    legacyTone: 'text-status-danger-fg bg-status-danger-bg border-status-danger-border',
    live: 'assertive',
  },
  action_pending: {
    Icon: LoaderCircle,
    tone: 'approval',
    legacyTone:
      'text-status-information-fg bg-status-information-bg border-status-information-border',
    live: 'polite',
  },
  action_committed: {
    Icon: CheckCircle2,
    tone: 'success',
    legacyTone: 'text-status-success-fg bg-status-success-bg border-status-success-border',
    live: 'polite',
  },
  action_failed: {
    Icon: AlertCircle,
    tone: 'danger',
    legacyTone: 'text-status-danger-fg bg-status-danger-bg border-status-danger-border',
    live: 'assertive',
  },
  superseded: {
    Icon: Clock3,
    tone: 'neutral',
    legacyTone: 'text-text-body bg-surface-soft border-border',
    live: 'polite',
  },
} as const satisfies Record<
  ExperienceStateName,
  {
    Icon: typeof LoaderCircle;
    tone: ExperienceStatusTone;
    legacyTone: string;
    live: 'polite' | 'assertive';
  }
>;

export interface ExperienceStateProps {
  state: ExperienceStateName;
  title: string;
  description: string;
  actions?: ReactNode;
  className?: string;
  compact?: boolean;
  titleLevel?: 2 | 3;
}

export function ExperienceState({
  state,
  title,
  description,
  actions,
  className,
  compact = false,
  titleLevel = 2,
}: ExperienceStateProps) {
  const { Icon, tone, legacyTone, live } = presentation[state];
  const isBusy = state === 'loading' || state === 'action_pending';
  const isError = state === 'denied' || state === 'error' || state === 'action_failed';
  const Title = titleLevel === 3 ? 'h3' : 'h2';

  return (
    <section
      className={cn(
        'rounded-overlay border p-5 motion-reduce:transition-none',
        legacyTone,
        EXPERIENCE_MOTION_CLASS,
        compact ? 'p-4' : 'min-h-40',
        className,
      )}
      role={isError ? 'alert' : 'status'}
      aria-live={live}
      aria-busy={isBusy || undefined}
      data-experience-state={state}
      data-experience-tone={tone}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            'experience-candidate-motion mt-0.5 h-5 w-5 shrink-0',
            isBusy && 'animate-spin motion-reduce:animate-none',
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <Title className="font-body text-card-title text-text-heading">{title}</Title>
          <p className="mt-1 font-body text-body-sm text-text-body">{description}</p>
          {actions ? <div className="mt-4 flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      </div>
    </section>
  );
}
